import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Calendar, MoreVertical, Trash2, Edit, Users, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { socialMediaCalendarsApi, clientsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SocialMediaCalendar, CreateSocialMediaCalendarRequest, Client } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const CALENDAR_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
]

export default function SocialMediaCalendarsPage() {
  useTranslation()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [calendarToDelete, setCalendarToDelete] = useState<SocialMediaCalendar | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [calendarToEdit, setCalendarToEdit] = useState<SocialMediaCalendar | null>(null)
  const [formData, setFormData] = useState<CreateSocialMediaCalendarRequest>({
    calendar_name: '',
    calendar_description: '',
    calendar_color: CALENDAR_COLORS[0],
    client_id: '',
  })

  const { data: calendars = [], isLoading } = useQuery({
    queryKey: ['socialMediaCalendars'],
    queryFn: () => socialMediaCalendarsApi.getAll({ active_only: true }),
  })

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAll(),
  })
  const clients = clientsData?.clients || []

  const createMutation = useMutation({
    mutationFn: (data: CreateSocialMediaCalendarRequest) => socialMediaCalendarsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendars'] })
      toast.success('Calendar created successfully')
      setCreateDialogOpen(false)
      resetForm()
    },
    onError: () => toast.error('Failed to create calendar'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSocialMediaCalendarRequest> }) =>
      socialMediaCalendarsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendars'] })
      toast.success('Calendar updated successfully')
      setEditDialogOpen(false)
      setCalendarToEdit(null)
      resetForm()
    },
    onError: () => toast.error('Failed to update calendar'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => socialMediaCalendarsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendars'] })
      toast.success('Calendar deleted successfully')
      setDeleteDialogOpen(false)
      setCalendarToDelete(null)
    },
    onError: () => toast.error('Failed to delete calendar'),
  })

  const resetForm = () => {
    setFormData({
      calendar_name: '',
      calendar_description: '',
      calendar_color: CALENDAR_COLORS[0],
      client_id: '',
    })
  }

  const handleCreate = () => {
    if (!formData.calendar_name.trim()) {
      toast.error('Calendar name is required')
      return
    }
    createMutation.mutate(formData)
  }

  const handleUpdate = () => {
    if (!calendarToEdit || !formData.calendar_name.trim()) {
      toast.error('Calendar name is required')
      return
    }
    updateMutation.mutate({ id: calendarToEdit.calendar_id, data: formData })
  }

  const openEditDialog = (calendar: SocialMediaCalendar) => {
    setCalendarToEdit(calendar)
    setFormData({
      calendar_name: calendar.calendar_name,
      calendar_description: calendar.calendar_description || '',
      calendar_color: calendar.calendar_color,
      client_id: calendar.client_id || '',
    })
    setEditDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Social Media Calendars</h1>
          <p className="text-muted-foreground">Manage your social media content calendars</p>
        </div>
        {canAddEdit('SOCIAL_MEDIA_CALENDARS') && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Calendar
          </Button>
        )}
      </div>

      {calendars.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No calendars yet</h3>
            <p className="text-muted-foreground mb-4">Create your first social media calendar to get started</p>
            {canAddEdit('SOCIAL_MEDIA_CALENDARS') && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Create Calendar
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {calendars.map((calendar) => (
            <CalendarCard key={calendar.calendar_id} calendar={calendar} onEdit={openEditDialog}
              onDelete={(c) => { setCalendarToDelete(c); setDeleteDialogOpen(true) }} canEdit={canAddEdit('SOCIAL_MEDIA_CALENDARS')} />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Calendar</DialogTitle>
            <DialogDescription>Create a new social media calendar</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" value={formData.calendar_name} onChange={(e) => setFormData({ ...formData, calendar_name: e.target.value })} placeholder="e.g., Q1 Campaign" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={formData.calendar_description || ''} onChange={(e) => setFormData({ ...formData, calendar_description: e.target.value })} placeholder="Calendar description..." />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="client">Client (Optional)</Label>
              <Select value={formData.client_id || ''} onValueChange={(v) => setFormData({ ...formData, client_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clients.map((c: Client) => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {CALENDAR_COLORS.map((color) => (
                  <button key={color} type="button" className={`w-8 h-8 rounded-full border-2 ${formData.calendar_color === color ? 'border-primary ring-2 ring-primary/50' : 'border-transparent'}`}
                    style={{ backgroundColor: color }} onClick={() => setFormData({ ...formData, calendar_color: color })} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm() }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Calendar</DialogTitle>
            <DialogDescription>Update calendar details</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input id="edit-name" value={formData.calendar_name} onChange={(e) => setFormData({ ...formData, calendar_name: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea id="edit-description" value={formData.calendar_description || ''} onChange={(e) => setFormData({ ...formData, calendar_description: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-client">Client</Label>
              <Select value={formData.client_id || ''} onValueChange={(v) => setFormData({ ...formData, client_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clients.map((c: Client) => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {CALENDAR_COLORS.map((color) => (
                  <button key={color} type="button" className={`w-8 h-8 rounded-full border-2 ${formData.calendar_color === color ? 'border-primary ring-2 ring-primary/50' : 'border-transparent'}`}
                    style={{ backgroundColor: color }} onClick={() => setFormData({ ...formData, calendar_color: color })} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setCalendarToEdit(null); resetForm() }}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Calendar</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{calendarToDelete?.calendar_name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => calendarToDelete && deleteMutation.mutate(calendarToDelete.calendar_id)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface CalendarCardProps {
  calendar: SocialMediaCalendar
  onEdit: (calendar: SocialMediaCalendar) => void
  onDelete: (calendar: SocialMediaCalendar) => void
  canEdit: boolean
}

function CalendarCard({ calendar, onEdit, onDelete, canEdit }: CalendarCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: calendar.calendar_color }}>
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <Link to={`/social-media/calendars/${calendar.calendar_id}`}>
                <CardTitle className="text-lg hover:text-primary transition-colors">{calendar.calendar_name}</CardTitle>
              </Link>
              {calendar.client_name && <p className="text-sm text-muted-foreground">{calendar.client_name}</p>}
            </div>
          </div>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(calendar)}><Edit className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(calendar)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {calendar.calendar_description && <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{calendar.calendar_description}</p>}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1"><Users className="h-4 w-4" />{calendar.account_count} accounts</div>
          <div className="flex items-center gap-1"><FileText className="h-4 w-4" />{calendar.post_count} posts</div>
        </div>
      </CardContent>
    </Card>
  )
}

